import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Shield, CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RoleBadge from '../../components/RoleBadge';
import { toast } from 'sonner';

/** Route each role to their most relevant first-landing page. */
const ROLE_FIRST_ROUTE = {
  owner:      '/dashboard',
  admin:      '/dashboard',
  sales:      '/leads',
  technician: '/dispatch',
  marketing:  '/engage',
};

const getFirstRoute = (role) => ROLE_FIRST_ROUTE[role] || '/dashboard';

const AcceptInvite = () => {
  const navigate = useNavigate();
  
  // Reliably read token from URL - use window.location.href as primary source
  // This prevents issues with React Router hydration stripping query params
  const getTokenAndVersionFromURL = () => {
    try {
      const url = new URL(window.location.href);
      const tokenFromURL = url.searchParams.get('token');
      const versionFromURL = url.searchParams.get('v');
      if (tokenFromURL) {
        console.log('✅ Token found in window.location.href:', tokenFromURL, 'version:', versionFromURL);
        return { token: tokenFromURL, version: versionFromURL };
      }

      const searchParams = new URLSearchParams(window.location.search);
      const tokenFromSearch = searchParams.get('token');
      const versionFromSearch = searchParams.get('v');
      if (tokenFromSearch) {
        console.log('✅ Token found in window.location.search:', tokenFromSearch, 'version:', versionFromSearch);
        return { token: tokenFromSearch, version: versionFromSearch };
      }

      console.error('❌ No token found in URL');
      return { token: null, version: null };
    } catch (error) {
      console.error('Error parsing token from URL:', error);
      return { token: null, version: null };
    }
  };

  const { token } = getTokenAndVersionFromURL();
  // All new invites are v2. v1 routes have been deprecated and removed.

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  
  // Form state for new users
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isExistingUser, setIsExistingUser] = useState(false);

  const loadInvite = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    try {
      // Call backend API to validate invite (v2 invites hit the v2 endpoint)
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const validatePath = '/api/invites-v2/validate';
      const response = await fetch(`${backendUrl}${validatePath}?token=${token}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'This invitation is invalid or has already been used');
        setLoading(false);
        return;
      }

      // Set invite data from backend response
      setInvite({
        id: data.inviteId,
        email: data.email,
        name: data.name,
        role: data.role,
        createdByName: data.createdBy,
        expiresAt: data.expiresAt
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading invite:', error);
      setError('Failed to load invitation');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAcceptInvite = async (e) => {
    e.preventDefault();

    if (!isExistingUser) {
      // Validate passwords for new users
      if (!password || password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }

      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    setAccepting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (isExistingUser) {
        // Existing user flow: Sign in with Firebase first
        try {
          console.log('[AcceptInvite] Signing in existing user:', invite.email);
          await signInWithEmailAndPassword(auth, invite.email, password);
        } catch (signInError) {
          console.error('[AcceptInvite] Firebase sign-in error:', signInError);
          if (signInError.code === 'auth/wrong-password') {
            toast.error('Incorrect password. Please try again.');
          } else if (signInError.code === 'auth/user-not-found') {
            toast.error('No account found with this email. Please create a new account.');
            setIsExistingUser(false);
          } else {
            toast.error('Failed to sign in. Please try again.');
          }
          setAccepting(false);
          return;
        }

        // After successful sign-in, call backend to accept invite
        console.log('[AcceptInvite] Submitting accept invite request', { mode: 'existing', email: invite.email });
        
        const acceptPath = '/api/invites-v2/accept';
        const response = await fetch(`${backendUrl}${acceptPath}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            mode: 'existing'
          })
        });

        // Parse JSON response once
        let data;
        try {
          data = await response.json();
          console.log('[AcceptInvite] Backend response', { status: response.status, data });
        } catch (jsonError) {
          console.error('[AcceptInvite] Failed to parse response JSON:', jsonError);
          toast.error('Unexpected server response. Please try again.');
          setAccepting(false);
          return;
        }

        // Check for success
        if (response.ok && data.success === true) {
          toast.success('Invitation accepted! Welcome to KRO.');
          setTimeout(() => {
            navigate(getFirstRoute(invite?.role));
          }, 500);
        } else {
          // Handle error response
          const errorMessage = data.message || data.detail || 'Failed to accept invitation';
          console.error('[AcceptInvite] Accept invite failed:', errorMessage);
          toast.error(errorMessage);
          setAccepting(false);
        }

      } else {
        // New user flow: Call backend to create account
        console.log('[AcceptInvite] Submitting accept invite request', { mode: 'new', email: invite.email });
        
        const acceptPath = '/api/invites-v2/accept';
        const response = await fetch(`${backendUrl}${acceptPath}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            password: password,
            mode: 'new'
          })
        });

        // Parse JSON response once
        let data;
        try {
          data = await response.json();
          console.log('[AcceptInvite] Backend response', { status: response.status, data });
        } catch (jsonError) {
          console.error('[AcceptInvite] Failed to parse response JSON:', jsonError);
          toast.error('Unexpected server response. Please try again.');
          setAccepting(false);
          return;
        }

        // Check for success
        if (response.ok && data.success === true) {
          // After successful account creation, sign in with Firebase
          try {
            console.log('[AcceptInvite] Auto-signing in new user:', invite.email);
            await signInWithEmailAndPassword(auth, invite.email, password);
            
            toast.success('Invitation accepted! Welcome to KRO.');
            setTimeout(() => {
              navigate(getFirstRoute(invite?.role));
            }, 500);
          } catch (signInError) {
            console.error('[AcceptInvite] Auto sign-in failed:', signInError);
            toast.warning('Account created! Please sign in.');
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          }
        } else {
          // Handle error response
          const errorMessage = data.message || data.detail || 'Failed to create account';
          console.error('[AcceptInvite] Accept invite failed:', errorMessage);
          
          // Special handling for email-already-exists
          if (data.code === 'email-already-exists') {
            toast.error('An account with this email already exists. Please sign in instead.');
            setIsExistingUser(true);
          } else {
            toast.error(errorMessage);
          }
          setAccepting(false);
        }
      }

    } catch (error) {
      // Network or unexpected errors
      console.error('[AcceptInvite] Unexpected error:', error);
      toast.error('Unexpected error accepting invitation. Please try again.');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-600 p-8 text-center">
          <CheckCircle className="h-16 w-16 text-white mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">You&apos;re Invited!</h1>
          <p className="text-blue-100">Join the KRO team</p>
        </div>

        {/* Invite Details */}
        <div className="p-8">
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Mail className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{invite.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Shield className="h-5 w-5 text-gray-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Role</p>
                <div className="mt-1">
                  <RoleBadge role={invite.role} />
                </div>
              </div>
            </div>

            {invite.name && (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="h-5 w-5 flex items-center justify-center">
                  <span className="text-gray-400">👤</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">{invite.name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Accept Form */}
          <form onSubmit={handleAcceptInvite} className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => setIsExistingUser(!isExistingUser)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {isExistingUser ? 'New user? Create account' : 'Already have an account? Sign in'}
              </button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isExistingUser ? 'Enter your password' : 'Create a password'}
                required
                disabled={accepting}
              />
            </div>

            {!isExistingUser && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  disabled={accepting}
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={accepting}
              className="w-full text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {accepting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isExistingUser ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                <>
                  {isExistingUser ? 'Sign In' : 'Accept Invitation'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-6">
            By accepting, you agree to KRO&apos;s Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
